import { resendRequestResetPasswordApi } from "@/api/services/main/auth";
import { Button } from "@/components/elements/Button";
import { RESEND_COOLDOWN_SECONDS, RESEND_RECOVER_EMAIL_COOLDOWN_STORAGE_KEY } from "@/constants/common";
import { LoginLogo } from "@/features/auth/components/LoginLogo";
import { AppRoutes } from "@/routes/config";
import { text } from "@/theme/colors";
import { getInitialCooldownOnReload } from "@/utils/cooldown";
import { showError } from "@/utils/error";
import { logout } from "@/utils/logout";
import { toast } from "@/utils/toast";
import { createUrlWithParams } from "@/utils/url";
import { Paper, Stack, Typography, styled } from "@mui/material";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

type TRecoverCompleteState = {
  email?: string;
  externalId?: string;
};

type TRecoverCompletePageProps = {};

export const RecoverCompletePage: FC<TRecoverCompletePageProps> = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as TRecoverCompleteState | null;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(() => getInitialCooldownOnReload(RESEND_RECOVER_EMAIL_COOLDOWN_STORAGE_KEY));

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (resendCooldown <= 0) {
      if (timerRef.current) clearInterval(timerRef.current);
      localStorage.removeItem(RESEND_RECOVER_EMAIL_COOLDOWN_STORAGE_KEY);
      return;
    }
    timerRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          if (timerRef.current) clearInterval(timerRef.current);
          localStorage.removeItem(RESEND_RECOVER_EMAIL_COOLDOWN_STORAGE_KEY);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [resendCooldown > 0]);

  useEffect(() => {
    return () => {
      localStorage.removeItem(RESEND_RECOVER_EMAIL_COOLDOWN_STORAGE_KEY);
    };
  }, []);

  const startCooldown = useCallback((seconds: number) => {
    const cooldownUntil = Date.now() + seconds * 1000;
    localStorage.setItem(RESEND_RECOVER_EMAIL_COOLDOWN_STORAGE_KEY, String(cooldownUntil));
    setResendCooldown(seconds);
  }, []);

  if (!state?.email || !state?.externalId) {
    return <Navigate to={AppRoutes.login} replace />;
  }

  const handleGoToLogin = async () => {
    await logout();
    const url = createUrlWithParams(AppRoutes.loginCredentials, { email: state.email, externalId: state.externalId });
    navigate(url);
  };

  const handleResendEmail = async () => {
    setIsSubmitting(true);
    if (resendCooldown > 0 || isSubmitting) return;
    try {
      const data = { email: state?.email ?? "", externalId: state?.externalId ?? "" };
      const rs = await resendRequestResetPasswordApi(data);
      toast.success(rs.message ?? "メールが再度送信されました。メールボックスをご確認ください。");
      startCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Stack gap="50px" alignItems="center">
      <LoginLogo />
      <Container>
        <Stack gap="48px" width={416}>
          <Stack gap={4}>
            <Typography variant="head20Bold" color={text.primary} textAlign="center">
              パスワード再発行メールの送信完了
            </Typography>
            <Typography variant="body14" color={text.secondary}>
              パスワードの再発行メールをご登録されているメールアドレス宛
              <br />
              に送信いたしました。
              <br />
              メールの内容に従ってパスワードを再発行してください。
              <br />
              <br />
              もしメールが届かない場合は、組織IDとメールアドレスにお間違
              <br />
              いがないか確認の上、改めて再発行してください。
            </Typography>
          </Stack>
          <Stack gap={4} alignItems="center">
            <Button type="button" size="sm" variant="primary" fullWidth onClick={handleGoToLogin}>
              ログイン画面へ
            </Button>
            <Stack alignItems="center" justifyContent="center">
              <Typography
                variant="body14"
                onClick={handleResendEmail}
                color={resendCooldown > 0 || isSubmitting ? text.disable : text.link}
                sx={{
                  cursor: resendCooldown > 0 || isSubmitting ? "default" : "pointer",
                  pointerEvents: resendCooldown > 0 || isSubmitting ? "none" : "auto",
                }}
              >
                {resendCooldown > 0 ? `メールの再送信（${resendCooldown}秒）` : "メールの再送信（ご案内メールが届かない場合）"}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </Container>
    </Stack>
  );
};

const Container = styled(Paper)`
  border-radius: 12px;
  padding: 24px;
  box-shadow:
    0 4px 4px -1px rgba(12, 12, 13, 0.05),
    0 4px 4px -1px rgba(12, 12, 13, 0.1) !important;
`;
